import React, { useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export type AttachmentKind = 'image' | 'pdf' | 'video' | 'other';

interface AttachmentLightboxProps {
  url: string;
  kind: AttachmentKind;
  filename?: string;
  mime?: string;
  onClose: () => void;
}

const AttachmentLightbox: React.FC<AttachmentLightboxProps> = ({
  url,
  kind,
  filename,
  mime,
  onClose,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={filename ?? 'Attachment preview'}
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full max-h-[92vh] flex flex-col gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between text-white">
          <p className="text-sm font-medium truncate pr-4">{filename ?? 'Attachment'}</p>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={url}
              download={filename}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download"
              className="p-2 rounded-full bg-white/10 hover:bg-white/20"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="p-2 rounded-full bg-white/10 hover:bg-white/20"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden rounded-xl bg-white dark:bg-neutral-900 flex items-center justify-center">
          {kind === 'image' && (
            <img
              src={url}
              alt={filename ?? 'attachment'}
              className="max-w-full max-h-[85vh] object-contain"
            />
          )}
          {kind === 'pdf' && (
            <iframe
              src={url}
              title={filename ?? 'PDF'}
              className="w-full h-[85vh] border-0"
            />
          )}
          {kind === 'video' && (
            <video
              src={url}
              controls
              className="max-w-full max-h-[85vh]"
            />
          )}
          {kind === 'other' && (
            <div className="p-10 text-center">
              <p className="mb-4 text-text-primary">
                Preview not available{mime ? ` for ${mime}` : ''}.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400"
              >
                <ArrowDownTrayIcon className="w-4 h-4" /> Open in new tab
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentLightbox;
